import { type DataSheetColumn } from '@components/ui/DataSheet';

/**
 * A vendor lane as the grids render it, and the columns that render it.
 *
 * Split from `LaneEditModal` so neither file exports both a component and helpers — the two
 * screens that show lanes (the Suppliers vendor pane and Settings -> Lanes) import from here
 * and from the modal beside it, and share one definition of both.
 */
export interface LaneRow {
    laneId?: string;
    warehouseId: string;
    source: string;
    destination: string;
    productionTime: number | null;
    qcTime: number | null;
    transitTime: number | null;
    deliveryTime: number | null;
    mode: string | null;
    /** Container cap for this leg in CBM. Null = no cap. */
    maxCbmPerShipment: number | null;
    /** How full a container should be before it is worth sending, % of the cap. */
    minFillPct?: number | null;
}

const days = (v: number | null) => (v != null ? `${v} days` : '—');

/** The lane columns every screen shows, before the optional CBM and Edit columns. */
export const LANE_COLUMNS: DataSheetColumn<LaneRow>[] = [
    { key: 'source', header: 'Port of Loading', width: 160 },
    { key: 'destination', header: 'Port of Unloading', width: 160 },
    /*
     * Where the goods are actually inwarded, as against the port of unloading beside it.
     * Those two look interchangeable and are not: the port is free text a user typed
     * ("Gurugram"), while this is the warehouse the lane is keyed on and what every lead
     * time is looked up by. Three APRICOT lanes read "Rajkot → Bengaluru / Gurugram / Thane"
     * and were otherwise indistinguishable from each other in the grid.
     *
     * The ID, not the name. It is the value the modal writes and the one the lane is keyed
     * on, the ids read well on their own (BAN_20, Prozo_B2B_Bhiwandi), and resolving names
     * would need a warehouse fetch that the INTERNAL-only picker cannot even satisfy — 13
     * live lanes land on MANUFACTURER warehouses it does not return.
     */
    { key: 'warehouseId', header: 'Destination Warehouse', width: 190 },
    {
        key: 'productionTime',
        header: 'Production Time',
        width: 130,
        align: 'right',
        render: days,
    },
    { key: 'qcTime', header: 'QC Time', width: 100, align: 'right', render: days },
    {
        key: 'transitTime',
        header: 'Transit Time',
        width: 120,
        align: 'right',
        render: days,
    },
    {
        key: 'deliveryTime',
        header: 'Delivery Time',
        width: 120,
        align: 'right',
        render: days,
    },
    {
        key: 'mode',
        header: 'Mode',
        width: 100,
        render: (v: string | null) => v || '—',
    },
];

/**
 * The full column set: the shared lanes, the CBM cap when the brand has CBM on, and the
 * Edit affordance. Both screens build their grid from this so the two cannot drift.
 */
export const buildLaneColumns = (
    cbmEnabled: boolean,
    onEdit: (lane: LaneRow) => void,
): DataSheetColumn<LaneRow>[] => [
    ...LANE_COLUMNS,
    ...(cbmEnabled
        ? [
              {
                  key: 'maxCbmPerShipment',
                  header: 'Max CBM / shipment',
                  width: 150,
                  align: 'right' as const,
                  render: (v: number | null) => (v != null ? `${v}` : '—'),
              },
              {
                  key: 'minFillPct',
                  header: 'Min fill',
                  width: 90,
                  align: 'right' as const,
                  render: (v: number | null | undefined) => (v != null ? `${v}%` : '—'),
              },
          ]
        : []),
    {
        key: 'laneId',
        header: '',
        width: 60,
        render: (_v: unknown, row: LaneRow) => (
            <button
                type="button"
                onClick={() => onEdit(row)}
                style={{
                    border: 'none',
                    background: 'transparent',
                    color: 'rgb(1, 119, 204)',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: 0,
                }}
            >
                Edit
            </button>
        ),
    },
];
